import { AccountInterface, Call, ProviderInterface, Contract, shortString } from 'starknet';
import { Decimal, DEFAULT_DECIMAL_PRECISION, Uint256 } from '../../datastructures';
import { REFERRAL_EMITTER_CONTRACT_ADDRESS } from '../../constants';
import { executeTransaction } from '../../utils/executeTransaction';
import { InvokeResponse } from '../../interfaces';
import NostraToken_ABI from './abi/NostraToken_abi.json';

export class NostraTokenContract {
  private contract: Contract;
  private precision: number;

  constructor(precision: number, address: string, providerOrAccount: ProviderInterface | AccountInterface) {
    this.contract = new Contract({ abi: NostraToken_ABI, address, providerOrAccount });

    this.precision = precision;
  }

  async balanceOf(address: string): Promise<Decimal | null> {
    if (!this.contract) {
      return null;
    }

    const result = await this.contract.balanceOf(address);

    return new Decimal(result[0] as Uint256, this.precision);
  }

  async totalSupply(): Promise<Decimal | null> {
    if (!this.contract) {
      return null;
    }

    const result = await this.contract.totalSupply();

    return new Decimal(result[0], this.precision);
  }

  async getTotalSupplyCap(): Promise<Decimal | null> {
    if (!this.contract) {
      return null;
    }

    const supply = await this.contract.getTotalSupplyCap();

    return new Decimal(supply[0] as Uint256, this.precision);
  }

  async mint(
    asset: string,
    amount: Decimal,
    to: string,
    referralCode: string,
    account: AccountInterface,
  ): Promise<InvokeResponse | null> {
    if (!this.contract) {
      return null;
    }

    const amountUint256 = amount.toUint256(this.precision);

    const calls = [
      {
        contractAddress: asset,
        entrypoint: 'approve',
        calldata: [this.contract.address, amountUint256.low, amountUint256.high],
      },
      {
        contractAddress: this.contract.address,
        entrypoint: 'mint',
        calldata: [to, amountUint256.low, amountUint256.high],
      },
    ] as Call[];

    if (referralCode) {
      calls.push({
        contractAddress: REFERRAL_EMITTER_CONTRACT_ADDRESS,
        entrypoint: 'refer',
        calldata: [shortString.encodeShortString(referralCode)],
      });
    }

    return executeTransaction(account, calls);
  }

  async burn(
    amount: Decimal,
    inputAmount: Decimal,
    account: AccountInterface,
    burnFrom: string,
  ): Promise<InvokeResponse | null> {
    if (!this.contract) {
      return null;
    }

    // If amount is Decimal.MAX_DECIMAL we don't want to reduce number of decimals to match token decimals, we want to keep amount to 18 decimals.
    // This way contract will properly recognize passed amount as Decimal.MAX_DECIMAL value.
    const amountUint256 = amount.toUint256(amount.equals(inputAmount) ? this.precision : DEFAULT_DECIMAL_PRECISION);

    const calls = [
      {
        contractAddress: this.contract.address,
        entrypoint: 'burn',
        calldata: [burnFrom, account.address, amountUint256.low, amountUint256.high],
      },
    ] as Call[];

    return executeTransaction(account, calls);
  }
}
